import { parseBRLInput } from '@/lib/format';
import type {
  MultiCotasCota,
  MultiCotasData,
  ProposalPayload,
} from '../consortium-groups/proposal-utils';

/**
 * Qual dos dois PDFs de proposta o North IA deve gerar.
 *
 * Os dois geradores já existem em `proposal-utils`: `generateProposalPdf`
 * (venda direta — "Olá NOME, aqui está sua proposta", com os blocos de sorteio
 * e de lance) e `generateMultiCotasPdf` (projeto estruturado — CONTRATAÇÃO,
 * APÓS A CONTEMPLAÇÃO e a tabela cota a cota). O que faltava era a ESCOLHA: o
 * chat mandava todo payload de `projeto` para o estruturado, então quem pedia
 * um crédito único recebia o PDF de projeto, cheio de "totais" que eram a
 * própria cota repetida.
 */
export type ModeloProposta = 'venda_direta' | 'venda_estruturada';

/**
 * Lê o valor em reais de um campo da tool.
 *
 * Os campos de lance vêm com o percentual colado no mesmo texto
 * ("R$ 50.000,00 (5%)"); sem cortar no parêntese o parse arrasta o percentual
 * junto e um lance de "R$ 0,00 (0%)" passaria a parecer que tem valor.
 */
const valorMonetario = (texto?: string | number | null): number => {
  if (typeof texto === 'number') return Number.isFinite(texto) ? texto : 0;
  if (!texto) return 0;
  return parseBRLInput(String(texto).split('(')[0]) ?? 0;
};

/** "1234 (180)" → 1234; "180 meses" → 180. */
const inteiroInicial = (valor?: string | number | null): number | undefined => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : undefined;
  const encontrado = /-?\d+/.exec(String(valor ?? ''));
  return encontrado ? Number(encontrado[0]) : undefined;
};

/** "18.00%" → 18. */
const percentual = (valor?: string | number | null): number | undefined => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : undefined;
  const limpo = String(valor ?? '').replace('%', '').replace(',', '.').trim();
  const numero = Number.parseFloat(limpo);
  return Number.isFinite(numero) ? numero : undefined;
};

/** "175x de R$ 4.000,00" → { parcelas: 175, valor: 4000 }. */
const lerParcelaPos = (texto?: string | null) => {
  const bruto = String(texto ?? '');
  const quantidade = /^\s*(\d+)\s*x/i.exec(bruto);
  const posicaoDe = bruto.indexOf('de ');
  return {
    parcelas: quantidade ? Number(quantidade[1]) : undefined,
    valor: valorMonetario(posicaoDe >= 0 ? bruto.slice(posicaoDe + 3) : bruto),
  };
};

/**
 * A tool acrescenta uma linha TOTAL no fim de `cotas[]` quando há mais de uma
 * cota. Ela não é uma cota — contá-la dobraria o crédito do projeto.
 */
const ehLinhaTotal = (cota: MultiCotasCota) =>
  cota.numero === -1 || /total/i.test(String(cota.grupo ?? ''));

const cotasReais = (projeto?: MultiCotasData | null): MultiCotasCota[] =>
  (projeto?.cotas ?? []).filter(cota => !ehLinhaTotal(cota));

/**
 * Quantas cotas o projeto tem de verdade.
 *
 * `resumo_projeto.quantidade_cotas` é a fonte; o array `cotas[]` vem colapsado
 * (cotas idênticas viram UMA entrada com `quantidade: N`), então contar linhas
 * diria "1 cota" para um projeto de dez cotas iguais.
 */
export function contarCotas(projeto?: MultiCotasData | null): number {
  const doResumo = Number(projeto?.resumo_projeto?.quantidade_cotas ?? 0);
  if (Number.isFinite(doResumo) && doResumo > 0) return doResumo;
  return cotasReais(projeto).reduce(
    (total, cota) => total + (Number(cota.quantidade) || 1),
    0,
  );
}

/**
 * A regra da escolha, decidida pelo DADO e não pelo rótulo que o modelo mandou.
 *
 * O modelo erra o nome com frequência (pede "projeto estruturado" para uma cota
 * só), mas a contagem de cotas e os valores de lance não têm como errar.
 *
 * Venda direta exige as três coisas:
 *  - exatamente UMA cota — o PDF estruturado só existe para somar cotas;
 *  - sem lance embutido — lance embutido é estruturação: encolhe o crédito
 *    liberado, e o PDF direto não tem onde mostrar essa diferença;
 *  - sem lance pago — a tool simula UM cenário só. Sem lance nenhum, esse
 *    cenário É o de sorteio, e o bloco "Simulação de contemplação por Sorteio"
 *    do PDF direto pode usá-lo direto. Com lance pago os números já são os do
 *    lance, e imprimi-los sob o título "Sorteio" seria mentira.
 */
export function escolherModeloProposta(projeto?: MultiCotasData | null): ModeloProposta {
  const cotas = cotasReais(projeto);
  if (cotas.length === 0) return 'venda_estruturada';
  if (contarCotas(projeto) !== 1) return 'venda_estruturada';

  const cota = cotas[0];
  if (valorMonetario(cota.lance_embutido) > 0) return 'venda_estruturada';
  if (valorMonetario(cota.lance_pago) > 0) return 'venda_estruturada';
  return 'venda_direta';
}

/**
 * Traduz a cota única do projeto para o payload que `generateProposalPdf` lê.
 *
 * Só faz sentido depois de `escolherModeloProposta` devolver 'venda_direta':
 * os blocos de lance saem desligados porque, por definição da regra, não há
 * lance nenhum nessa cota.
 */
export function converterParaPropostaDireta(
  projeto: MultiCotasData,
  clientName?: string,
): ProposalPayload {
  const cota = cotasReais(projeto)[0] ?? {};
  const prazo = inteiroInicial(cota.prazo) ?? 0;
  const parcelaPos = lerParcelaPos(cota.parcela_pos);
  const parcelasRestantes = parcelaPos.parcelas ?? Math.max(0, prazo - 1);
  const credito = valorMonetario(cota.credito);

  return {
    groupNumber: inteiroInicial(cota.grupo),
    categoria: cota.categoria,
    prazo,
    taxaAdmin: percentual(cota.taxa_admin),
    valorCredito: credito,
    // O PDF direto lê `parcelaJunta` como a parcela do que foi contratado;
    // com uma cota só, é a parcela dela mesma.
    parcelaJunta: valorMonetario(cota.parcela),
    juncaoCota: '1 cota',
    mesesContemplacao: Math.max(1, prazo - parcelasRestantes),
    creditoSorteio: valorMonetario(cota.credito_liberado) || credito,
    saldoDevedorSorteio: valorMonetario(cota.saldo_devedor),
    parcelaAPagarSorteio: parcelaPos.valor,
    parcelasRestantesSorteio: parcelasRestantes,
    lanceFixoAtivo: false,
    lanceLivreAtivo: false,
    lanceFidelidadeAtivo: false,
    clientName,
    generatedAt: new Date().toISOString(),
  };
}
